import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FertilizerProduct } from "@/types/products";
import {
    Button,
    getKeyValue,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from "@heroui/react";
import { IrrigationSystem } from "@/types/irrigationSystem";
import TextInput from "@/components/inputs/TextInput";
import { isNumber } from "@/lib/utils/numbers";
import {
    targetRateNToProdutcRate,
    productRateToTargetRateN,
    speedToDepth,
    depthToSpeed,
} from "@/lib/utils/operations";
import { OperationFormType } from "@/lib/form/operation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFill } from "@fortawesome/free-solid-svg-icons";
import SelectMultipleInput from "@/components/inputs/SelectMultipleInput";
import { FertilizerType } from "@/types/fertilizer";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { ApplicationMethod } from "@/types/enums";

function ApplicationTable({
    selectedProduct,
    irrigationSystem,
    loading,
    hasSectors,
    readOnly,
}: {
    selectedProduct?: FertilizerProduct;
    irrigationSystem?: IrrigationSystem;
    loading?: boolean;
    hasSectors?: boolean;
    readOnly?: boolean;
}) {
    const { t } = useTranslation("operations");
    const { fieldIsAdvanced } = useFieldDashboard() ?? {};

    const f = useFormContext<OperationFormType>();

    const [method, table, batch_fill, template] = f.watch([
        "method",
        "table",
        "batch_fill",
        "template",
    ]);

    const isFertigation = method === ApplicationMethod.Fertigation;
    const { formatUnit } = useI18n();
    const COLUMNS = useMemo(() => {
        const columns = [];
        if (fieldIsAdvanced) {
            columns.push({ key: "sector", label: t("sector") });
            columns.push({ key: "treatment", label: t("treatment") });
        }
        if (isFertigation) {
            columns.push({ key: "start", label: t("start") });
            columns.push({ key: "stop", label: t("stop") });
        } else {
            columns.push({ key: "zone_number", label: t("zone") });
            columns.push({ key: "type", label: t("type") });
        }

        columns.push({
            key: "target_rate_n",
            label: `${t("nitrogenRate")} (${formatUnit("lb_ac", "kg_ha")})`,
        });
        columns.push({
            key: "product_rate",
            label:
                selectedProduct?.fertilizer_type == FertilizerType.dry
                    ? `${t("dryProductRate")} (${formatUnit("lb_ac", "kg_ha")})`
                    : `${t("liquidProductRate")} (${formatUnit("gal_ac", "L_ha")})`,
        });
        if (isFertigation) {
            columns.push({ key: "speed", label: `${t("speed")} (%)` });
            columns.push({
                key: "depth",
                label: `${t("depth")} (${formatUnit("in", "cm")})`,
            });
        }
        return columns;
    }, [
        fieldIsAdvanced,
        formatUnit,
        isFertigation,
        selectedProduct?.fertilizer_type,
        t,
    ]);

    const { fields, append, remove } = useFieldArray<
        OperationFormType,
        "table"
    >({
        name: "table",
        control: f.control,
    });

    const [showBatchFill, setShowBatchFill] = useState<boolean>(false);

    const handleBatchFill = useCallback(
        (key: "target_rate_n" | "product_rate" | "speed" | "depth") => {
            const rowsToUpdate = table
                ?.map((row, index) => ({
                    ...row,
                    index,
                }))
                .filter((row) => {
                    if (batch_fill.zones) {
                        return batch_fill.zones.includes(
                            row.zone_number.toString(),
                        );
                    }
                    return true;
                })
                .filter((row) => {
                    if (batch_fill.sectors) {
                        return batch_fill.sectors.includes(
                            row.sector.toString(),
                        );
                    }
                    return true;
                })
                .filter((row) => {
                    if (
                        batch_fill.land_type &&
                        batch_fill.land_type.length != 2
                    ) {
                        if (batch_fill.land_type.includes("irrigated")) {
                            return row.irrigated;
                        } else if (batch_fill.land_type.includes("dryland")) {
                            return !row.irrigated;
                        }
                    }
                    return true;
                });

            rowsToUpdate?.forEach((row) => {
                f.setValue(`table.${row.index}.${key}`, batch_fill[key], {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });

                if (key === "speed") {
                    f.setValue(
                        `table.${row.index}.depth`,
                        speedToDepth(batch_fill.speed, irrigationSystem),
                    );
                }
                if (key === "depth") {
                    f.setValue(
                        `table.${row.index}.speed`,
                        depthToSpeed(batch_fill.depth, irrigationSystem),
                    );
                }
                if (key === "target_rate_n") {
                    f.setValue(
                        `table.${row.index}.product_rate`,
                        targetRateNToProdutcRate(
                            batch_fill.target_rate_n,
                            selectedProduct,
                        ),
                    );
                }
                if (key === "product_rate") {
                    f.setValue(
                        `table.${row.index}.target_rate_n`,
                        productRateToTargetRateN(
                            batch_fill.product_rate,
                            selectedProduct,
                        ),
                    );
                }
            });
        },
        [table, f, batch_fill, irrigationSystem, selectedProduct],
    );

    const sectorOptions = useMemo(() => {
        return Array.from(
            new Set(
                table?.filter((row) => !!row.sector).map((row) => row.sector),
            ),
        )
            .map((sector) => ({ id: sector, description: sector }))
            .sort();
    }, [table]);

    const zoneOptions = useMemo(() => {
        return Array.from(new Set(table?.map((row) => row.zone_number)))
            .map((zone) => ({ id: zone, description: zone }))
            .sort();
    }, [table]);

    return (
        <>
            <div className="flex flex-col gap-1 items-start">
                <Button
                    size="sm"
                    color="primary"
                    onPress={() => setShowBatchFill(!showBatchFill)}
                >
                    {showBatchFill ? "Hide Batch Fill" : "Show Batch Fill"}
                </Button>
                {showBatchFill && (
                    <div className="flex flex-col gap-1">
                        {!isFertigation && (
                            <div className="flex flex-row gap-1 ">
                                <SelectMultipleInput
                                    options={[
                                        {
                                            id: "irrigated",
                                            description: t("irrigated"),
                                        },
                                        {
                                            id: "dryland",
                                            description: t("dryland"),
                                        },
                                    ]}
                                    name="batch_fill.land_type"
                                    size="sm"
                                    label={t("type")}
                                    placeholder={t("type")}
                                    labelPlacement="outside"
                                    isDisabled={readOnly}
                                />
                                {hasSectors && (
                                    <SelectMultipleInput
                                        options={sectorOptions}
                                        name="batch_fill.sectors"
                                        size="sm"
                                        label={t("zone")}
                                        labelPlacement="outside"
                                        isDisabled={readOnly}
                                    />
                                )}
                                {template && (
                                    <SelectMultipleInput
                                        options={zoneOptions}
                                        name="batch_fill.zones"
                                        size="sm"
                                        label={t("zone")}
                                        labelPlacement="outside"
                                        isDisabled={readOnly}
                                    />
                                )}
                            </div>
                        )}
                        <div className="flex flex-row gap-2 ">
                            <TextInput
                                name="batch_fill.target_rate_n"
                                size="sm"
                                label={t("nitrogenRate")}
                                labelPlacement="outside"
                                type="number"
                                classNames={{
                                    inputWrapper: "pr-0",
                                    input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                }}
                                placeholder="0"
                                isDisabled={readOnly}
                                endContent={
                                    <Button
                                        isIconOnly
                                        as={"div"}
                                        size="sm"
                                        color="primary"
                                        onPress={() =>
                                            handleBatchFill("target_rate_n")
                                        }
                                    >
                                        <FontAwesomeIcon icon={faFill} />
                                    </Button>
                                }
                            />
                            <TextInput
                                name="batch_fill.product_rate"
                                size="sm"
                                label={t("productRate")}
                                labelPlacement="outside"
                                type="number"
                                placeholder="0"
                                classNames={{
                                    inputWrapper: "pr-0",
                                    input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                }}
                                isDisabled={readOnly}
                                endContent={
                                    <Button
                                        isIconOnly
                                        as={"div"}
                                        size="sm"
                                        color="primary"
                                        onPress={() =>
                                            handleBatchFill("product_rate")
                                        }
                                    >
                                        <FontAwesomeIcon icon={faFill} />
                                    </Button>
                                }
                            />
                            {isFertigation && (
                                <>
                                    <TextInput
                                        name="batch_fill.speed"
                                        size="sm"
                                        label={t("speed")}
                                        labelPlacement="outside"
                                        type="number"
                                        placeholder="0"
                                        classNames={{
                                            inputWrapper: "pr-0",
                                            input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                        }}
                                        isDisabled={readOnly}
                                        endContent={
                                            <Button
                                                isIconOnly
                                                as={"div"}
                                                size="sm"
                                                color="primary"
                                                onPress={() =>
                                                    handleBatchFill("speed")
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faFill}
                                                />
                                            </Button>
                                        }
                                    />
                                    <TextInput
                                        name="batch_fill.depth"
                                        size="sm"
                                        label={t("depth")}
                                        labelPlacement="outside"
                                        type="number"
                                        decimalPlaces={2}
                                        placeholder="0"
                                        classNames={{
                                            inputWrapper: "pr-0",
                                            input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                        }}
                                        isDisabled={readOnly}
                                        endContent={
                                            <Button
                                                isIconOnly
                                                as={"div"}
                                                size="sm"
                                                color="primary"
                                                onPress={() =>
                                                    handleBatchFill("depth")
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faFill}
                                                />
                                            </Button>
                                        }
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Table
                aria-label="Application Table"
                classNames={{
                    td: "p-1",
                }}
            >
                <TableHeader columns={COLUMNS}>
                    {(item) => (
                        <TableColumn key={item.key}>{item.label}</TableColumn>
                    )}
                </TableHeader>
                <TableBody
                    isLoading={loading}
                    loadingContent={<Spinner label="Loading..." />}
                    emptyContent={
                        <div className="text-red-500 italic">
                            No table found, please contact support
                        </div>
                    }
                >
                    {fields.map((item, index) => (
                        <TableRow key={item.id}>
                            {(columnKey) => {
                                if (columnKey === "target_rate_n") {
                                    return (
                                        <TableCell>
                                            <TextInput
                                                key={item.id}
                                                aria-label="Target Rate N"
                                                size="sm"
                                                type="number"
                                                name={`table.${index}.target_rate_n`}
                                                placeholder="Target Rate"
                                                decimalPlaces={2}
                                                classNames={{
                                                    input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                                }}
                                                isDisabled={readOnly}
                                                onChange={(e) => {
                                                    f.setValue(
                                                        `table.${index}.product_rate`,
                                                        targetRateNToProdutcRate(
                                                            isNumber(
                                                                e.target.value,
                                                            )
                                                                ? Number(
                                                                      e.target
                                                                          .value,
                                                                  )
                                                                : undefined,
                                                            selectedProduct,
                                                        ),
                                                        {
                                                            shouldValidate:
                                                                true,
                                                        },
                                                    );
                                                }}
                                                hideErrorMessage
                                            />
                                        </TableCell>
                                    );
                                }

                                if (columnKey === "product_rate") {
                                    return (
                                        <TableCell>
                                            <TextInput
                                                key={item.id}
                                                aria-label="Product Rate"
                                                size="sm"
                                                name={`table.${index}.product_rate`}
                                                type="number"
                                                placeholder="Product Rate"
                                                decimalPlaces={2}
                                                classNames={{
                                                    input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                                }}
                                                isDisabled={readOnly}
                                                hideErrorMessage
                                                onChange={(e) => {
                                                    f.setValue(
                                                        `table.${index}.target_rate_n`,
                                                        productRateToTargetRateN(
                                                            isNumber(
                                                                e.target.value,
                                                            )
                                                                ? Number(
                                                                      e.target
                                                                          .value,
                                                                  )
                                                                : undefined,
                                                            selectedProduct,
                                                        ),
                                                        {
                                                            shouldValidate:
                                                                true,
                                                        },
                                                    );
                                                }}
                                            />
                                        </TableCell>
                                    );
                                }

                                if (columnKey === "speed") {
                                    return (
                                        <TableCell>
                                            <TextInput
                                                key={item.id}
                                                aria-label="Speed"
                                                size="sm"
                                                name={`table.${index}.speed`}
                                                type="number"
                                                placeholder="Speed"
                                                classNames={{
                                                    input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                                }}
                                                decimalPlaces={2}
                                                hideErrorMessage
                                                isDisabled={readOnly}
                                                onChange={(e) => {
                                                    f.setValue(
                                                        `table.${index}.depth`,
                                                        speedToDepth(
                                                            isNumber(
                                                                e.target.value,
                                                            )
                                                                ? Number(
                                                                      e.target
                                                                          .value,
                                                                  )
                                                                : undefined,
                                                            irrigationSystem,
                                                        ),
                                                        {
                                                            shouldValidate:
                                                                true,
                                                        },
                                                    );
                                                }}
                                            />
                                        </TableCell>
                                    );
                                }
                                if (columnKey === "depth") {
                                    return (
                                        <TableCell>
                                            <TextInput
                                                key={item.id}
                                                aria-label="Depth"
                                                size="sm"
                                                name={`table.${index}.depth`}
                                                type="number"
                                                placeholder="Depth"
                                                hideErrorMessage
                                                classNames={{
                                                    input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                                }}
                                                decimalPlaces={2}
                                                isDisabled={readOnly}
                                                onChange={(e) => {
                                                    f.setValue(
                                                        `table.${index}.speed`,
                                                        depthToSpeed(
                                                            isNumber(
                                                                e.target.value,
                                                            )
                                                                ? Number(
                                                                      e.target
                                                                          .value,
                                                                  )
                                                                : undefined,
                                                            irrigationSystem,
                                                        ),
                                                        {
                                                            shouldValidate:
                                                                true,
                                                        },
                                                    );
                                                }}
                                            />
                                        </TableCell>
                                    );
                                }

                                if (columnKey === "start" && !hasSectors) {
                                    return (
                                        <TableCell>
                                            <TextInput
                                                key={item.id}
                                                aria-label="Start"
                                                size="sm"
                                                name={`table.${index}.start`}
                                                classNames={{
                                                    input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                                }}
                                                type="number"
                                                isDisabled={readOnly}
                                            />
                                        </TableCell>
                                    );
                                }
                                if (columnKey === "stop" && !hasSectors) {
                                    return (
                                        <TableCell>
                                            <TextInput
                                                key={item.id}
                                                aria-label="Stop"
                                                size="sm"
                                                name={`table.${index}.stop`}
                                                classNames={{
                                                    input: "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                                }}
                                                type="number"
                                                isDisabled={readOnly}
                                            />
                                        </TableCell>
                                    );
                                }

                                if (columnKey === "type") {
                                    return (
                                        <TableCell>
                                            {item.irrigated
                                                ? t("irrigated")
                                                : t("dryland")}
                                        </TableCell>
                                    );
                                }

                                if (columnKey === "zone_number") {
                                    return (
                                        <TableCell>
                                            {item.zone_number ??
                                                item.zone ??
                                                "--"}
                                        </TableCell>
                                    );
                                }

                                return (
                                    <TableCell>
                                        {getKeyValue(item, columnKey)}
                                    </TableCell>
                                );
                            }}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {isFertigation && (
                <div className="flex flex-row gap-2">
                    <Button
                        size="sm"
                        color="danger"
                        onPress={() => remove(-1)}
                        isDisabled={fields.length === 1}
                    >
                        Remove row
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        onPress={() => append({})}
                    >
                        Add row
                    </Button>
                </div>
            )}
        </>
    );
}

export default ApplicationTable;
